import Header from "@/components/Header";
import { SITE_URL } from "@/lib/site-config";
import Hero from "@/components/Hero";
import CompanyFeatures from "@/components/CompanyFeatures";
import TrustSection from "@/components/TrustSection";
import ProductCard from "@/components/ProductCard";
import SEO from "@/components/SEO";
import { breadcrumbList } from "@/utils/breadcrumb";
import SiteFooter from "@/components/SiteFooter";
import { IndustryInsights } from "@/components/ContentBlock";
import { SEOBlogSection, PharmaceuticalFAQ } from "@/components/SEOBlogSection";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { exportProductCatalog } from "@/utils/downloadUtils";
import { 
  Search, 
  ArrowRight, 
  Award, 
  Globe, 
  Beaker, 
  Shield,
  Phone,
  Mail,
  MapPin
} from "lucide-react";

const Index = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const { t } = useLanguage();
  const { toast } = useToast();

  // Live category counts from the products table — replaces hardcoded
  // 150 / 200 / 100 that drifted out of sync with the catalog.
  const [categoryCounts, setCategoryCounts] = useState<{ human: number; intermediates: number; veterinary: number }>({
    human: 0,
    intermediates: 0,
    veterinary: 0,
  });
  useEffect(() => {
    (async () => {
      const fetchCount = async (category: string) => {
        const { count } = await supabase
          .from('products')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'active')
          .eq('category', category);
        return count ?? 0;
      };
      const [human, intermediates, veterinary] = await Promise.all([
        fetchCount('Human APIs'),
        fetchCount('Intermediates'),
        fetchCount('Veterinary APIs'),
      ]);
      setCategoryCounts({ human, intermediates, veterinary });
    })();
  }, []);
  
  // Note: the previous version of this file had a useEffect that
  // imperatively mutated document.title and meta tags with hard-coded
  // strings — it ran AFTER Helmet's render and stomped over the values
  // emitted by the <SEO> component above. That dead pattern is removed;
  // <SEO> now owns every head meta tag declaratively, which means
  // canonical, hreflang, OG dims, and the pipe-stuffed title actually
  // reach the document.
  
  // Sample products data
  const featuredProducts = [
    {
      name: "Atorvastatin Calcium",
      casNumber: "134523-00-5",
      category: "Human APIs",
      description: "High-purity statin compound for cholesterol management. Pharmaceutical grade with complete documentation.",
      availability: "In Stock" as const,
      certifications: ["FDA DMF", "CEP", "ISO 9001"],
      price: {
        value: 285.50,
        unit: "kg",
        currency: "USD",
        moq: 25
      }
    },
    {
      name: "Amoxicillin Trihydrate",
      casNumber: "26787-78-0",
      category: "Human APIs",
      description: "Beta-lactam antibiotic for bacterial infections. GMP manufactured with full regulatory support.",
      availability: "In Stock" as const,
      certifications: ["FDA", "EMA", "WHO-GMP"],
      price: {
        value: 165.00,
        unit: "kg",
        currency: "USD",
        moq: 50
      }
    },
    {
      name: "Ivermectin",
      casNumber: "70288-86-7",
      category: "Veterinary APIs",
      description: "Broad-spectrum antiparasitic agent for veterinary applications. High efficacy and purity guaranteed.",
      availability: "On Demand" as const,
      certifications: ["USP", "EP", "Veterinary"],
      price: {
        value: 1250.00,
        unit: "kg",
        currency: "USD",
        moq: 10
      }
    },
    {
      name: "Fluconazole",
      casNumber: "86386-73-4",
      category: "Human APIs",
      description: "Antifungal medication intermediate with excellent stability profile and regulatory compliance.",
      availability: "In Stock" as const,
      certifications: ["ICH", "FDA DMF", "CEP"],
      price: {
        value: 420.75,
        unit: "kg",
        currency: "USD",
        moq: 25
      }
    }
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleDownloadCatalog = () => {
    // Reuse the products page CSV export — it writes a real file with the
    // current catalog rather than the previous href="#" placeholder that
    // downloaded an empty document.
    try {
      exportProductCatalog('all');
      toast({
        title: "Catalog downloaded",
        description: "Product catalog CSV saved to your downloads.",
      });
    } catch {
      toast({
        title: "Download failed",
        description: "Please try again from /products or contact us.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="WHO-GMP API Manufacturer, Pune, India"
        description="WHO-GMP, ISO 9001:2015, US FDA DMF, EDQM CEP certified API manufacturer in Pune. 560+ pharmaceutical APIs, intermediates, peptides, veterinary. CDMO services to 50+ countries."
        keywords="pharmaceutical APIs, drug intermediates, veterinary APIs, pharmaceutical manufacturing, ISO certified, India pharma, CAS numbers, GMP, FDA approved, European compliance, North America supply, South America distribution, pharmaceutical supplier, CDMO, contract manufacturing"
        canonical={`${SITE_URL}/`}
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": `${SITE_URL}/#webpage`,
              "url": `${SITE_URL}/`,
              "name": "Longshine Boi-Tech | Pharmaceutical APIs | Intermediates | Peptides | Veterinary APIs | CDMO | Manufacturer in India",
              "isPartOf": { "@id": `${SITE_URL}/#website` },
              "about": { "@id": `${SITE_URL}/#org` },
              /* Reference the rich ImageObject node below so Google
                 Image Search can attribute the hero image to this page
                 with proper caption + geo signals instead of treating
                 it as a bare URL string. */
              "primaryImageOfPage": { "@id": `${SITE_URL}/#hero-image` },
              "image": { "@id": `${SITE_URL}/#hero-image` },
            },
            /* ImageObject — geo-tagged hero with explicit caption so the
               image earns separate ranking signal for Google Image queries
               like "WHO-GMP API manufacturer Pune photo" + sets up the
               attribution chain to our Organization node. */
            {
              "@type": "ImageObject",
              "@id": `${SITE_URL}/#hero-image`,
              "url": `${SITE_URL}/og-image.jpg`,
              "contentUrl": `${SITE_URL}/og-image.jpg`,
              "width": 1920,
              "height": 1080,
              "encodingFormat": "image/jpeg",
              "caption": "WHO-GMP & ISO 9001:2015 certified pharmaceutical API manufacturing facility — Longshine Boi-Tech, Pune, Maharashtra, India",
              "creditText": "Longshine Boi-Tech Pvt. Ltd.",
              "creator": { "@id": `${SITE_URL}/#org` },
              "copyrightHolder": { "@id": `${SITE_URL}/#org` },
              "copyrightNotice": `© ${new Date().getFullYear()} Longshine Boi-Tech Pvt. Ltd. All rights reserved. Images licensed for editorial use; commercial reuse requires written permission.`,
              "copyrightYear": new Date().getFullYear(),
              "acquireLicensePage": `${SITE_URL}/contact?subject=image-licensing`,
              "contentLocation": {
                "@type": "Place",
                "name": "Plot 45, Boi-Tech Industrial Zone, Pune-Mumbai Highway, Pune 411001, Maharashtra, India",
                "geo": { "@type": "GeoCoordinates", "latitude": 18.5204, "longitude": 73.8567 },
              },
              "representativeOfPage": true,
              "license": `${SITE_URL}/return-policy`,
            },
            breadcrumbList([["Home", "/"]]),
            // FAQPage schema — the homepage already has buyer-facing Q&A
            // content (see SEOBlogSection.PharmaceuticalFAQ). Marking it up
            // as FAQPage JSON-LD makes Google eligible to surface these
            // answers as expandable rich results directly in the SERP and
            // makes them quote-able by ChatGPT / Perplexity / Gemini.
            // Keep questions/answers short — Google truncates beyond ~300
            // chars per answer.
            {
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Is Longshine Boi-Tech GMP certified?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes. Our Pune facility holds an active WHO-GMP certificate (renewed annually since 2014), ISO 9001:2015 certification, and a CDSCO manufacturing license. We also maintain 60+ FDA DMF Type II filings and multiple CEP/EDQM certificates for European supply.",
                  },
                },
                {
                  "@type": "Question",
                  "name": "Do you supply pharmaceutical APIs to the United States?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes — we hold 60+ active FDA DMF Type II filings. US sponsors can reference our manufacturing process under confidentiality via a Letter of Authorization. We supply human APIs, intermediates, and veterinary compounds against USP/NF specifications.",
                  },
                },
                {
                  "@type": "Question",
                  "name": "What is the minimum order quantity for APIs?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "MOQs vary by molecule and synthetic route — published per-SKU on each product page. For most human APIs we ship from 1 kg pilot quantities up to multi-tonne commercial campaigns. Custom synthesis and CDMO contracts start at lab scale (10g-100g).",
                  },
                },
                {
                  "@type": "Question",
                  "name": "How long do API shipments take to reach the US or EU?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Standard ocean freight from Mumbai is 4-6 weeks to the US East Coast and 4-5 weeks to Rotterdam/Hamburg. Express air freight is 7-10 days globally. Stocking arrangements available for high-volume buyers.",
                  },
                },
                {
                  "@type": "Question",
                  "name": "Can you handle custom API synthesis or contract manufacturing (CDMO)?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes. We operate as a CDMO across process development, scale-up, and commercial manufacturing. Capabilities include cGMP API synthesis, peptide synthesis, fermentation, and key intermediate manufacturing. Engagements start under MNDA and a feasibility assessment.",
                  },
                },
                {
                  "@type": "Question",
                  "name": "Which pharmacopoeias do you support?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "USP/NF, Ph. Eur., IP (Indian Pharmacopoeia), JP (Japanese Pharmacopoeia), and BP. Every Certificate of Analysis references the buyer's chosen pharmacopoeia. We run the relevant methods in-house in our QC laboratory.",
                  },
                },
              ],
            },
          ],
        }}
      />
      <Header />
      <Hero />

      {/* Trust strip — second thing visitors see after the hero. Audit
          flagged absence of testimonials + "countries served" + buyer-
          industry counters as a top-3 trust gap that suppresses
          first-touch conversion. */}
      <TrustSection />

      {/* Product Categories Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">
              {t('categories.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              {t('categories.subtitle')}
            </p>
            
            {/* Search bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto relative">
              <Search className="w-6 h-6 absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('categories.search.placeholder')}
                className="w-full pl-12 pr-6 py-4 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary text-lg shadow-card"
              />
              <Button 
                type="submit" 
                className="absolute right-2 top-2 bottom-2 px-6" 
                variant="professional"
              >
                {t('common.search')}
              </Button>
            </form>
          </div>

          {/* Product Categories */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <Link to="/products/human-apis" className="bg-card p-8 rounded-xl border border-border shadow-card hover:shadow-elevated transition-all duration-300 group cursor-pointer block">
              <div className="mb-6">
                <Beaker className="w-12 h-12 text-primary mb-4 group-hover:text-accent transition-colors" />
                <h3 className="text-2xl font-semibold text-card-foreground mb-2">{t('categories.human_apis.title')}</h3>
                <p className="text-muted-foreground">{t('categories.human_apis.description')}</p>
              </div>
              <div className="flex items-center justify-between">
                <Badge variant="secondary">{categoryCounts.human || '—'}{t('categories.products_count')}</Badge>
                <ArrowRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link to="/products/intermediates" className="bg-card p-8 rounded-xl border border-border shadow-card hover:shadow-elevated transition-all duration-300 group cursor-pointer block">
              <div className="mb-6">
                <Shield className="w-12 h-12 text-primary mb-4 group-hover:text-accent transition-colors" />
                <h3 className="text-2xl font-semibold text-card-foreground mb-2">{t('categories.intermediates.title')}</h3>
                <p className="text-muted-foreground">{t('categories.intermediates.description')}</p>
              </div>
              <div className="flex items-center justify-between">
                <Badge variant="secondary">{categoryCounts.intermediates || '—'}{t('categories.products_count')}</Badge>
                <ArrowRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link to="/products/veterinary" className="bg-card p-8 rounded-xl border border-border shadow-card hover:shadow-elevated transition-all duration-300 group cursor-pointer block">
              <div className="mb-6">
                <Globe className="w-12 h-12 text-primary mb-4 group-hover:text-accent transition-colors" />
                <h3 className="text-2xl font-semibold text-card-foreground mb-2">{t('categories.veterinary.title')}</h3>
                <p className="text-muted-foreground">{t('categories.veterinary.description')}</p>
              </div>
              <div className="flex items-center justify-between">
                <Badge variant="secondary">{categoryCounts.veterinary || '—'}{t('categories.products_count')}</Badge>
                <ArrowRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>

          {/* Long-tail category strip — the 5 catalogue categories not
              featured above. Rendered as compact pill-cards so they don't
              dominate the visual hierarchy of the 3 headline categories,
              but each one passes a sitewide internal link to its landing
              page (these were 100% orphaned from the homepage pre-audit). */}
          <div className="mt-8">
            <div className="text-xs uppercase tracking-wider text-muted-foreground text-center mb-3">
              Also in our catalogue
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Link to="/products/peptides" className="px-4 py-2 rounded-full bg-card border border-border text-sm text-foreground hover:bg-muted/60 hover:border-primary/40 transition-colors">
                Peptides
              </Link>
              <Link to="/products/excipients" className="px-4 py-2 rounded-full bg-card border border-border text-sm text-foreground hover:bg-muted/60 hover:border-primary/40 transition-colors">
                Excipients
              </Link>
              <Link to="/products/amino-acids" className="px-4 py-2 rounded-full bg-card border border-border text-sm text-foreground hover:bg-muted/60 hover:border-primary/40 transition-colors">
                Amino Acids
              </Link>
              <Link to="/products/nootropics" className="px-4 py-2 rounded-full bg-card border border-border text-sm text-foreground hover:bg-muted/60 hover:border-primary/40 transition-colors">
                Nootropics
              </Link>
              <Link to="/products/research-chemicals" className="px-4 py-2 rounded-full bg-card border border-border text-sm text-foreground hover:bg-muted/60 hover:border-primary/40 transition-colors">
                Research Chemicals
              </Link>
            </div>
          </div>

          {/* Quick links to the high-intent B2B landing pages. These keep
              "GMP certified API supplier" and "API supplier [region]"
              queries one click from the homepage hero. */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <Link to="/certifications" className="px-4 py-2 rounded-full bg-primary/5 border border-primary/20 text-sm text-primary hover:bg-primary/10 transition-colors">
              GMP / FDA DMF / CEP certifications →
            </Link>
            <Link to="/markets" className="px-4 py-2 rounded-full bg-primary/5 border border-primary/20 text-sm text-primary hover:bg-primary/10 transition-colors">
              Markets we serve (US · EU · LatAm · APAC · MEA) →
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">
              {t('featured.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('featured.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {featuredProducts.map((product, index) => (
              <ProductCard key={index} {...product} />
            ))}
          </div>

          <div className="text-center">
            <Link to="/products">
              <Button size="lg" variant="outline" className="px-8">
                {t('featured.view_all')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <CompanyFeatures />

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-light to-primary opacity-90"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              {t('cta.title')}
            </h2>
            <p className="text-xl mb-8 text-primary-foreground/90">
              {t('cta.subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/contact">
                <Button size="lg" variant="accent" className="px-8 py-4 text-lg h-auto">
                  {t('cta.quote_now')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="px-8 py-4 text-lg h-auto border-white text-white hover:bg-white hover:text-primary"
                onClick={handleDownloadCatalog}
              >
                {t('cta.download_catalog')}
              </Button>
            </div>

            {/* Contact info. Phone tile hidden — voice channel disabled. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-center max-w-2xl mx-auto">
              <div className="flex items-center justify-center space-x-3">
                <Mail className="w-6 h-6" />
                <span className="text-lg">info@longshineboitech.com</span>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <MapPin className="w-6 h-6" />
                <span className="text-lg">Maharashtra, India</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Blog Articles */}
      <SEOBlogSection />

      {/* Pharmaceutical FAQ with Schema */}
      <PharmaceuticalFAQ />

      {/* Industry Insights - SEO Content */}
      <IndustryInsights />

      <SiteFooter />
    </div>
  );
};

export default Index;